import {ILocationService} from 'angular';
import {CustomerCache} from 'components/service/customer.cache.types';
import {Hold} from 'components/service/product.types';
import nxModule from 'nxModule';
import {NxRouteService} from 'routes/NxRouteService';
import {CommandService} from 'shared/utils/command/command.types';
import {Confirmation} from 'shared/common/confirmation.types';
import {HttpService} from 'shared/utils/httpService';

import templateUrl from './edit-product-hold.template.html';

interface HoldPage {
  result: Hold[];
  totalCount: number;
}

interface EditableProduct {
  id: number;
  productNumber: string;
  status: string;
  balance: number;
  holdsBalance: number;
}

/**
 * Edits the amount of an existing MoneyLock hold.
 *
 * The Holds tab this is reached from serves both deposit accounts and term deposits, so the
 * product is resolved from whichever route parameter is present. Amount rules are enforced
 * server-side by UpdateManualHold; the max here is only a convenience.
 */
export class EditProductHoldController {
  hold: Hold | null = null;
  product: EditableProduct | null = null;
  amount: number | null = null;
  updateRemarks: string | null = null;

  private readonly customerId: string;
  private readonly holdId: number;
  private readonly accountId: string | undefined;
  private readonly depositId: string | undefined;

  constructor(
    $route: NxRouteService,
    private $location: ILocationService,
    private http: HttpService,
    private command: CommandService,
    private confirmation: Confirmation,
    private customerCache: CustomerCache
  ) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const params = $route.current.params;
    this.customerId = params['customerId'] as string;
    this.holdId = Number(params['holdId']);
    this.accountId = params['accountId'] as string | undefined;
    this.depositId = params['depositId'] as string | undefined;
  }

  private get productId(): number {
    return Number(this.accountId ?? this.depositId);
  }

  private get isTermDeposit(): boolean {
    return !this.accountId && Boolean(this.depositId);
  }

  /** Available balance plus the amount this hold already holds, mirroring the server-side rule. */
  get maxAmount(): number {
    if (!this.product || !this.hold) {
      return 0;
    }
    return this.product.balance + this.hold.amount;
  }

  async $onInit(): Promise<void> {
    const products = this.isTermDeposit
      ? await this.customerCache.termDeposits(this.customerId).toPromise()
      : await this.customerCache.depositAccounts(this.customerId).toPromise();

    this.product = (products as unknown as EditableProduct[])
      .find(p => p.id === this.productId) ?? null;

    // Filter by hold id server-side; paging the product's holds could miss it. productId is kept
    // so the hold must also belong to the product in the route.
    const holds = await this.http
      .get<HoldPage>(`/products/holds?pageNo=0&pageSize=1&productIds=${this.productId}&holdIds=${this.holdId}`)
      .toPromise();

    this.hold = holds.result[0] ?? null;

    if (!this.isEditable(this.hold)) {
      this.redirectBack();
      return;
    }

    // Pre-fill with the existing amount so the user edits rather than re-enters it.
    this.amount = this.hold ? this.hold.amount : null;
  }

  private isEditable(hold: Hold | null): boolean {
    return hold !== null
      && hold.type === 'MANUAL'
      && hold.status === 'ACTIVE'
      && hold.subType === 'MONEY_LOCK';
  }

  redirectBack(): void {
    const productPath = this.isTermDeposit
      ? `term-deposits/${this.depositId}`
      : `accounts/${this.accountId}`;
    this.$location.path(`/customer/${this.customerId}/${productPath}`);
  }

  save(): void {
    if (!this.hold) {
      return;
    }

    this.confirmation(`Do you want to update the hold amount to ${this.amount}?`, async () => {
      await this.command.execute('UpdateManualHold', {
        holdId: this.holdId,
        amount: this.amount,
        updateRemarks: this.updateRemarks
      }).toPromise();

      if (this.isTermDeposit) {
        this.customerCache.termDeposits(this.customerId).refetch();
      } else {
        this.customerCache.depositAccounts(this.customerId).refetch();
      }

      this.redirectBack();
    });
  }
}

nxModule.component('editProductHold', {
  templateUrl,
  controller: EditProductHoldController
});
